import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  confirmSignUp,
  getCurrentUser,
  resendSignUpCode,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
} from 'aws-amplify/auth';
import api from '@/lib/api';

export interface User {
  id: string;
  email: string;
  fullName?: string;
  avatarUrl?: string;
  role: 'user' | 'admin' | 'superadmin';
  plan: 'free' | 'pro' | 'agency';
  creditsBalance: number;
  creditsSubscription: number;
  creditsPurchased: number;
  creditsBonus: number;
  onboardingCompleted: boolean;
  onboardingStep: number;
  expertiseAreas: string[];
  notificationPreferences: {
    email_weekly: boolean;
    new_posts: boolean;
    credit_low: boolean;
    payment?: boolean;
  };
  timezone?: string;
}

export interface SignupResult {
  requiresConfirmation: boolean;
  username: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, fullName: string) => Promise<SignupResult>;
  confirmSignup: (email: string, code: string) => Promise<void>;
  resendSignupCode: (email: string) => Promise<void>;
  googleLogin: () => Promise<void>;
  linkedinLogin: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  completeOnboarding: (step?: number) => Promise<void>;
  deleteAccount: () => Promise<void>;
  initializeSession: () => Promise<void>;
  deductCredits: (amount: number) => void;
  addCredits: (amount: number, type: 'subscription' | 'purchased' | 'bonus') => void;
}

interface AuthMeResponse {
  data: {
    id: string;
    email: string;
    full_name: string | null;
    avatar_url: string | null;
    role: 'user' | 'admin' | 'superadmin';
    plan: {
      slug: 'free' | 'pro' | 'agency' | null;
    };
    credits: {
      total: number;
      subscription: number;
      purchased: number;
      bonus: number;
    };
    onboarding: {
      completed: boolean;
      step: number;
    };
    expertise_areas?: string[] | null;
    notification_preferences?: {
      email_weekly?: boolean;
      new_posts?: boolean;
      credit_low?: boolean;
      payment?: boolean;
    };
    timezone?: string | null;
  };
}

function mapAuthMeToUser(payload: AuthMeResponse['data']): User {
  return {
    id: payload.id,
    email: payload.email,
    fullName: payload.full_name ?? undefined,
    avatarUrl: payload.avatar_url ?? undefined,
    role: payload.role,
    plan: payload.plan.slug ?? 'free',
    creditsBalance: payload.credits.total,
    creditsSubscription: payload.credits.subscription,
    creditsPurchased: payload.credits.purchased,
    creditsBonus: payload.credits.bonus,
    onboardingCompleted: payload.onboarding.completed,
    onboardingStep: payload.onboarding.step,
    expertiseAreas: payload.expertise_areas ?? [],
    notificationPreferences: {
      email_weekly: payload.notification_preferences?.email_weekly ?? true,
      new_posts: payload.notification_preferences?.new_posts ?? true,
      credit_low: payload.notification_preferences?.credit_low ?? true,
      payment: payload.notification_preferences?.payment ?? true,
    },
    timezone: payload.timezone ?? undefined,
  };
}

async function fetchProfile(): Promise<User> {
  const response = await api.get<AuthMeResponse>('/auth/me');
  return mapAuthMeToUser(response.data.data);
}

async function recoverExistingCognitoSession(): Promise<User | null> {
  try {
    const existingUser = await getCurrentUser();
    if (existingUser) {
      return await fetchProfile();
    }
    return null;
  } catch (error: any) {
    if (error?.name === 'UserUnAuthenticatedException' || error?.name === 'NotAuthorizedException') {
      return null;
    }
    throw error;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { isSignedIn } = await signIn({ username: email, password });
          if (isSignedIn) {
            const profile = await fetchProfile();
            set({ user: profile, isAuthenticated: true, isLoading: false });
          } else {
            set({ isLoading: false });
          }
        } catch (error: any) {
          console.error('Login error:', error);
          set({ error: error.message || 'Login failed', isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await signOut();
          set({ user: null, isAuthenticated: false, isLoading: false });
        } catch (error) {
          set({ error: 'Logout failed', isLoading: false });
        }
      },

      signup: async (email, password, fullName) => {
        set({ isLoading: true, error: null });
        try {
          const result = await signUp({
            username: email,
            password,
            options: {
              userAttributes: {
                email,
                name: fullName,
              },
            },
          });

          set({ isLoading: false });

          return {
            requiresConfirmation: result.nextStep.signUpStep === 'CONFIRM_SIGN_UP',
            username: email,
          };
        } catch (error: any) {
          console.error('Signup error:', error);
          set({ error: error.message || 'Signup failed', isLoading: false });
          throw error;
        }
      },

      confirmSignup: async (email, code) => {
        set({ isLoading: true, error: null });
        try {
          await confirmSignUp({ username: email, confirmationCode: code });
          set({ isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Confirmation failed', isLoading: false });
          throw error;
        }
      },

      resendSignupCode: async (email) => {
        set({ isLoading: true, error: null });
        try {
          await resendSignUpCode({ username: email });
          set({ isLoading: false });
        } catch (error: any) {
          set({ error: error.message || 'Unable to resend code', isLoading: false });
          throw error;
        }
      },

      googleLogin: async () => {
        set({ isLoading: true, error: null });
        try {
          const existingProfile = await recoverExistingCognitoSession();
          if (existingProfile) {
            set({ user: existingProfile, isAuthenticated: true, isLoading: false });
            return;
          }

          // No usable existing session: continue with hosted Google OAuth.
          await signInWithRedirect({
            provider: { custom: 'Google' },
            options: { prompt: 'SELECT_ACCOUNT' },
          });
        } catch (error: any) {
          set({ error: error.message || 'Google login failed', isLoading: false });
          throw error;
        }
      },

      linkedinLogin: async () => {
        set({ isLoading: true, error: null });
        try {
          const existingProfile = await recoverExistingCognitoSession();
          if (existingProfile) {
            set({ user: existingProfile, isAuthenticated: true, isLoading: false });
            return;
          }

          // No usable existing session: continue with hosted LinkedIn OAuth.
          await signInWithRedirect({ provider: { custom: 'LinkedIn' } });
        } catch (error: any) {
          set({ error: error.message || 'LinkedIn login failed', isLoading: false });
          throw error;
        }
      },

      updateProfile: async (data) => {
        const { user } = get();
        if (!user) {
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const payload: Record<string, unknown> = {};
          if (data.fullName !== undefined) {
            payload.full_name = data.fullName;
          }
          if (data.avatarUrl !== undefined) {
            payload.avatar_url = data.avatarUrl;
          }
          if (data.notificationPreferences !== undefined) {
            payload.notification_preferences = data.notificationPreferences;
          }
          if (data.timezone !== undefined) {
            payload.timezone = data.timezone;
          }
          if (data.expertiseAreas !== undefined) {
            payload.expertise_areas = data.expertiseAreas;
          }

          await api.put('/auth/me', payload);
          const refreshed = await fetchProfile();
          set({ user: refreshed, isAuthenticated: true, isLoading: false });
        } catch (error: any) {
          set({ error: error?.message || 'Profile update failed', isLoading: false });
          throw error;
        }
      },

      initializeSession: async () => {
        set({ isLoading: true, error: null });
        try {
          await getCurrentUser();
          const profile = await fetchProfile();
          set({ user: profile, isAuthenticated: true, isLoading: false });
        } catch (_error) {
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      completeOnboarding: async (step = 3) => {
        set({ isLoading: true, error: null });
        try {
          await api.put('/auth/onboarding', {
            step,
            completed: true,
          });
          const refreshed = await fetchProfile();
          set({ user: refreshed, isAuthenticated: true, isLoading: false });
        } catch (error: any) {
          set({ error: error?.message || 'Unable to complete onboarding', isLoading: false });
          throw error;
        }
      },

      deleteAccount: async () => {
        set({ isLoading: true, error: null });
        try {
          await api.delete('/auth/me');
          await signOut();
          set({ user: null, isAuthenticated: false, isLoading: false });
        } catch (error: any) {
          set({ error: error?.message || 'Unable to delete account', isLoading: false });
          throw error;
        }
      },

      deductCredits: (amount) => {
        const { user } = get();
        if (user && user.creditsBalance >= amount) {
          set({
            user: {
              ...user,
              creditsBalance: user.creditsBalance - amount,
            },
          });
        }
      },

      addCredits: (amount, type) => {
        const { user } = get();
        if (user) {
          const updates: Partial<User> = {
            creditsBalance: user.creditsBalance + amount,
          };

          switch (type) {
            case 'subscription':
              updates.creditsSubscription = user.creditsSubscription + amount;
              break;
            case 'purchased':
              updates.creditsPurchased = user.creditsPurchased + amount;
              break;
            case 'bonus':
              updates.creditsBonus = user.creditsBonus + amount;
              break;
          }

          set({ user: { ...user, ...updates } });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
